import { useEffect, useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';

import { useNavigate } from '@/components/Link/Link';
import Button from '@/design_system/Button';
import Checkbox from '@/design_system/Checkbox';
import Dialog from '@/design_system/Dialog';
import { Footer, FooterButtons } from '@/design_system/Footer';
import { InputSelect } from '@/design_system/InputSelect/InputSelect';
import InputText from '@/design_system/InputText';
import Message from '@/design_system/Message';
import Stack from '@/design_system/Stack';
import { useShowToast } from '@/design_system/Toast';
import Paragraph from '@/design_system/Typography/Paragraph';
import {
  getWorkflowStoreTypeLabel,
  getWorkflowTypeLabel,
  useCreateWorkflow,
} from '@/models/workflow';

type WorkflowType = 'client' | 'client_via_store' | 'store';
type StoreType = 'internal' | 'external';

const WORKFLOW_TYPE_OPTIONS: { id: WorkflowType }[] = [
  { id: 'client' },
  { id: 'client_via_store' },
  { id: 'store' },
];

const STORE_TYPE_OPTIONS: StoreType[] = ['internal', 'external'];

export const CreateWorkflowDialog = ({
  isOpen,
  setIsOpen,
}: {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}) => {
  const { t, i18n } = useLingui();
  const navigate = useNavigate();
  const showToast = useShowToast();
  const { mutateAsync: createWorkflow, isPending } = useCreateWorkflow();

  const [name, setName] = useState('');
  const [type, setType] = useState<WorkflowType>('client');
  const [storeTypes, setStoreTypes] = useState<StoreType[]>(['internal', 'external']);
  const [showErrors, setShowErrors] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setName('');
      setType('client');
      setStoreTypes(['internal', 'external']);
      setShowErrors(false);
    }
  }, [isOpen]);

  const requiresStoreTypes = type === 'store' || type === 'client_via_store';
  const nameError = !name.trim()
    ? t({
        id: 'settings.organization.workflows.workflows.create.error.name',
        message: 'Please enter a name',
      })
    : undefined;
  const storeTypesError =
    requiresStoreTypes && storeTypes.length === 0
      ? t({
          id: 'settings.organization.workflows.workflows.create.error.store-types',
          message: 'Select at least one store type',
        })
      : undefined;
  const hasError = !!nameError || !!storeTypesError;

  const toggleStoreType = (storeType: StoreType) => {
    setStoreTypes((current) =>
      current.includes(storeType)
        ? current.filter((value) => value !== storeType)
        : [...current, storeType]
    );
  };

  const handleSubmit = async () => {
    if (hasError) {
      setShowErrors(true);

      return;
    }

    try {
      const workflow = await createWorkflow({
        name: name.trim(),
        type,
        storeTypes: requiresStoreTypes ? storeTypes : undefined,
      });

      setIsOpen(false);
      navigate(`/settings_beta/workflows/workflows/${workflow.id}`);
    } catch {
      showToast({
        text: (
          <Trans id="settings.organization.workflows.workflows.create.error">
            Could not create the workflow. Please try again.
          </Trans>
        ),
        type: 'error',
      });
    }
  };

  return (
    <Dialog
      isOpen={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          if (isPending) {
            return;
          }

          setIsOpen(false);
        }
      }}
      title={t({
        id: 'settings.organization.workflows.workflows.create.title',
        message: 'Create workflow',
      })}
      style={{ width: '32rem' }}
    >
      <Stack gap="1rem" style={{ padding: '1.5rem' }}>
        <InputText
          label={t({
            id: 'settings.organization.workflows.workflows.create.name.label',
            message: 'Name',
          })}
          value={name}
          onChange={setName}
          isInvalid={showErrors && !!nameError}
          error={showErrors ? nameError : undefined}
          isDisabled={isPending}
        />

        <InputSelect
          label={t({
            id: 'settings.organization.workflows.workflows.create.type.label',
            message: 'Type',
          })}
          options={WORKFLOW_TYPE_OPTIONS}
          value={WORKFLOW_TYPE_OPTIONS.find((option) => option.id === type)}
          getOptionValue={(option) => option.id}
          getOptionLabel={(option) => i18n._(getWorkflowTypeLabel(option.id))}
          onChange={(option) => {
            if (!option) {
              return;
            }

            setType(option.id);
          }}
          isDisabled={isPending}
          isClearable={false}
        />

        {requiresStoreTypes && (
          <Stack gap="0.5rem">
            <Paragraph weight="medium">
              <Trans id="settings.organization.workflows.workflows.create.store-types.label">
                Store types
              </Trans>
            </Paragraph>
            <Stack row gap="1rem">
              {STORE_TYPE_OPTIONS.map((storeType) => (
                <Checkbox
                  key={storeType}
                  isSelected={storeTypes.includes(storeType)}
                  onChange={() => toggleStoreType(storeType)}
                  isDisabled={isPending}
                >
                  {i18n._(getWorkflowStoreTypeLabel(storeType))}
                </Checkbox>
              ))}
            </Stack>
            {showErrors && storeTypesError && <Message type="error">{storeTypesError}</Message>}
          </Stack>
        )}
      </Stack>

      <Footer>
        <FooterButtons variant="full">
          <Button
            variant="secondary"
            size="medium"
            onPress={() => setIsOpen(false)}
            disabled={isPending}
          >
            <Trans id="settings.organization.workflows.workflows.create.cancel">Cancel</Trans>
          </Button>
          <Button
            variant="primary"
            size="medium"
            onPress={() => void handleSubmit()}
            isLoading={isPending}
          >
            <Trans id="settings.organization.workflows.workflows.create.submit">Create</Trans>
          </Button>
        </FooterButtons>
      </Footer>
    </Dialog>
  );
};
